import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

import Home from "@/pages/marketing/Home";
import Features from "@/pages/marketing/Features";
import Pricing from "@/pages/marketing/Pricing";
import WhyPhoneFirst from "@/pages/marketing/WhyPhoneFirst";
import TemplatesGallery from "@/pages/marketing/TemplatesGallery";
import Contact from "@/pages/marketing/Contact";
import Blog from "@/pages/marketing/Blog";
import BlogPost from "@/pages/marketing/BlogPost";
import Login from "@/pages/app/Login";
import Dashboard from "@/pages/app/Dashboard";
import Builder from "@/pages/app/Builder";
import Profile from "@/pages/app/Profile";
import AppTemplates from "@/pages/app/AppTemplates";
import PaymentSuccess from "@/pages/app/PaymentSuccess";
import PaymentCancel from "@/pages/app/PaymentCancel";

const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    const finish = async () => {
      if (!sessionId) return navigate("/login", { replace: true });
      try {
        const { data } = await client.post("/auth/session", { session_id: sessionId });
        setUser(data.user);
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/app", { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    };
    finish();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-ho-bg">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full border-[3px] border-ho-primary border-t-transparent animate-spin" />
        <p className="text-sm text-ho-muted font-medium">Signing you in…</p>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ho-bg">
        <div className="w-9 h-9 rounded-full border-[3px] border-ho-primary border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/features" element={<Features />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/why-phone-first" element={<WhyPhoneFirst />} />
      <Route path="/templates" element={<TemplatesGallery />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/login" element={<Login />} />
      <Route path="/app" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/app/templates" element={<ProtectedRoute><AppTemplates /></ProtectedRoute>} />
      <Route path="/app/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/app/build/:id" element={<ProtectedRoute><Builder /></ProtectedRoute>} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
